import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EventForm from './EventForm';
import ImportReviewPanel from './ImportReviewPanel';
import LoginForm from './LoginForm';
import { getAllCities } from '../data/citiesCoordinates';
import apiService, { setAdminToken, clearAdminToken, hasAdminToken } from '../services/api';
import {
  extractEventsArray,
  validateAndNormalizeImport,
  type NormalizedImportEvent,
} from '../utils/importEventsJson';
import { isFacebookEventsCsv, parseFacebookEventsCsv } from '../utils/importFacebookCsv';
import './Admin.css';

interface Event {
  id: number | string;
  name: string;
  city: string;
  date: string;
  time?: string;
  type: string;
  price?: string;
  showPrice?: boolean;
  address?: string;
  link?: string;
  description?: string;
  multiDay?: boolean;
  isMultiDay?: boolean;
  endDate?: string;
  organizer?: string;
  phone?: string;
  email?: string;
  coordinates?: [number, number];
  createdAt?: string;
  updatedAt?: string;
  originalEventId?: number;
}

interface Submission {
  id: number;
  name: string;
  city: string;
  date: string;
  time?: string;
  type?: string;
  price?: string;
  showPrice?: boolean;
  address?: string;
  link?: string;
  description?: string;
  isMultiDay?: boolean;
  endDate?: string;
  status: string;
  createdAt: string;
  reviewedAt?: string;
}

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [activeTab, setActiveTab] = useState<'events' | 'submissions'>('events');
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [editingSubmission, setEditingSubmission] = useState<Submission | null>(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(true);
  const [filters, setFilters] = useState({
    city: '',
    type: '',
    date: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [importReviewQueue, setImportReviewQueue] = useState<NormalizedImportEvent[] | null>(null);

  // Sprawdzenie czy użytkownik jest już zalogowany
  useEffect(() => {
    const authStatus = localStorage.getItem('adminAuth');
    if (authStatus === 'true' && hasAdminToken()) {
      setIsAuthenticated(true);
      setShowLoginForm(false);
    } else {
      if (authStatus === 'true') {
        localStorage.removeItem('adminAuth');
        clearAdminToken();
      }
      setShowLoginForm(true);
    }
  }, []);

  // Ładowanie eventów z API
  useEffect(() => {
    const loadEvents = async () => {
      try {
        const eventsFromAPI = await apiService.getEvents();
        setEvents(eventsFromAPI);
        setFilteredEvents(eventsFromAPI);
      } catch (error) {
        console.error('Failed to load events from API:', error);
        setEvents([]);
        setFilteredEvents([]);
      }
    };

    const loadSubmissions = async () => {
      try {
        const submissionsFromAPI = await apiService.getSubmissions();
        setSubmissions(submissionsFromAPI);
      } catch (error) {
        console.error('Failed to load submissions from API:', error);
        const message = error instanceof Error ? error.message : '';
        if (message.includes('Sesja wygasła') || message.includes('autoryzacji')) {
          setIsAuthenticated(false);
          setShowLoginForm(true);
          return;
        }
        setSubmissions([]);
      }
    };

    if (isAuthenticated) {
      loadEvents();
      loadSubmissions();
    }
  }, [isAuthenticated]);

  // Filtrowanie eventów
  useEffect(() => {
    let filtered = [...events];

    // Wyszukiwanie artystów/zespołów
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(event => 
        event.name.toLowerCase().includes(query)
      );
    }

    if (filters.city) {
      filtered = filtered.filter(event => 
        event.city.toLowerCase().includes(filters.city.toLowerCase())
      );
    }

    if (filters.type) {
      filtered = filtered.filter(event => event.type === filters.type);
    }

    if (filters.date) {
      filtered = filtered.filter(event => event.date === filters.date);
    }

    setFilteredEvents(filtered);
  }, [filters, events, searchQuery]);

  const handleLoginSuccess = (token?: string) => {
    localStorage.setItem('adminAuth', 'true');
    if (token) setAdminToken(token);
    setIsAuthenticated(true);
    setShowLoginForm(false);
  };

  const handleLoginCancel = () => {
    navigate('/');
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuth');
    clearAdminToken();
    navigate('/');
  };

  const addEvent = async (eventData: Event) => {
    try {
      const newEvent = await apiService.createEvent(eventData);
      setEvents(prev => [...prev, newEvent]);
      setShowEventForm(false);
    } catch (error) {
      console.error('Failed to add event:', error);
      alert('Błąd podczas dodawania eventu');
    }
  };

  const editEvent = (event: Event) => {
    setEditingEvent(event);
  };

  const updateEvent = async (updatedEvent: Event) => {
    try {
      const updated = await apiService.updateEvent(updatedEvent.id, updatedEvent);
      setEvents(prev => prev.map(event => 
        event.id === updated.id ? updated : event
      ));
      setEditingEvent(null);
    } catch (error) {
      console.error('Failed to update event:', error);
      alert('Błąd podczas aktualizacji eventu');
    }
  };

  const deleteEvent = async (eventId: number | string) => {
    if (window.confirm('Czy na pewno chcesz usunąć to wydarzenie?')) {
      try {
        const numericId = typeof eventId === 'number' ? eventId : parseInt(eventId.toString());
        await apiService.deleteEvent(numericId);
        setEvents(prev => prev.filter(event => event.id !== eventId));
      } catch (error) {
        console.error('Failed to delete event:', error);
        alert('Błąd podczas usuwania eventu');
      }
    }
  };

  const handleFilterChange = (filterName: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      city: '',
      type: '',
      date: ''
    });
    setSearchQuery('');
  };

  // Listy dla filtrów
  const eventTypes = [
    { value: 'Koncert', label: 'Koncert 🎵' },
    { value: 'Kultura', label: 'Kultura 🎭' },
    { value: 'Jedzenie', label: 'Jedzenie 🍽️' },
    { value: 'Festiwal', label: 'Festiwal 🎪' },
    { value: 'Bary', label: 'Bary 🍻' },
    { value: 'Sport', label: 'Sport ⚽' },
    { value: 'Nauka', label: 'Nauka 🔬' },
    { value: 'Kino', label: 'Kino 🎬' },
    { value: 'Targi', label: 'Targi 🏪' },
    { value: 'Kluby', label: 'Kluby 🎤' }
  ];

  const allCities = getAllCities();

  // Funkcje do obsługi zgłoszeń
  const handleApproveSubmission = async (submissionId: number) => {
    try {
      await apiService.approveSubmission(submissionId);
      // Odśwież listę zgłoszeń
      const updatedSubmissions = await apiService.getSubmissions();
      setSubmissions(updatedSubmissions);
      // Odśwież listę eventów
      const updatedEvents = await apiService.getEvents();
      setEvents(updatedEvents);
      alert('Zgłoszenie zatwierdzone i dodane do eventów!');
    } catch (error) {
      console.error('Error approving submission:', error);
      alert('Błąd podczas zatwierdzania zgłoszenia');
    }
  };

  const handleRejectSubmission = async (submissionId: number) => {
    try {
      await apiService.rejectSubmission(submissionId);
      // Odśwież listę zgłoszeń
      const updatedSubmissions = await apiService.getSubmissions();
      setSubmissions(updatedSubmissions);
      alert('Zgłoszenie odrzucone');
    } catch (error) {
      console.error('Error rejecting submission:', error);
      alert('Błąd podczas odrzucania zgłoszenia');
    }
  };

  const editSubmission = (submission: Submission) => {
    setEditingSubmission(submission);
  };

  const updateSubmission = async (updated: Event) => {
    if (!editingSubmission) return;
    try {
      const saved = await apiService.updateSubmission(editingSubmission.id, {
        name: updated.name,
        city: updated.city,
        date: updated.date,
        time: updated.time,
        type: updated.type,
        price: updated.price,
        showPrice: updated.showPrice,
        address: updated.address,
        link: updated.link,
        description: updated.description,
        isMultiDay: updated.isMultiDay,
        endDate: updated.endDate,
      });
      setSubmissions((prev) =>
        prev.map((s) => (s.id === saved.id ? { ...s, ...saved } : s))
      );
      setEditingSubmission(null);
      alert('Zgłoszenie zaktualizowane');
    } catch (error) {
      console.error('Failed to update submission:', error);
      alert('Błąd podczas aktualizacji zgłoszenia');
    }
  };

  // Funkcje eksportu/importu JSON
  const exportEventsToJSON = () => {
    const dataStr = JSON.stringify(events, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `events-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    alert(`Wyeksportowano ${events.length} wydarzeń do pliku JSON`);
  };

  const parseImportFile = (file: File): Promise<NormalizedImportEvent[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (loadEvent) => {
        try {
          const text = loadEvent.target?.result as string;
          const isCsv =
            file.name.toLowerCase().endsWith('.csv') ||
            isFacebookEventsCsv(text);

          let rawList: unknown[] | null = null;

          if (isCsv && isFacebookEventsCsv(text)) {
            const { events, skipped: csvSkipped } = parseFacebookEventsCsv(text, {
              type: 'Festiwal',
            });
            if (csvSkipped > 0) {
              alert(`CSV Facebook: pominięto ${csvSkipped} wierszy (brak nazwy/daty/miasta lub duplikat).`);
            }
            rawList = events;
          } else if (isCsv) {
            reject(new Error(
              'Nieobsługiwany CSV.\n\n' +
              'Oczekiwany eksport listy wydarzeń Facebooka\n' +
              '(kolumny z linkiem facebook.com/events/... oraz datą i nazwą).'
            ));
            return;
          } else {
            const parsed = JSON.parse(text);
            rawList = extractEventsArray(parsed);
            if (!rawList) {
              reject(new Error(
                'Nieprawidłowy format JSON.\n\n' +
                'Obsługiwane formaty:\n' +
                '• tablica wydarzeń: [{ "name", "city", "date", ... }]\n' +
                '• eksport Live Nation: { "events": [ ... ] }\n' +
                '• Facebook Events Scraper: [{ "name", "utcStartDate", "location.city", "url", ... }]\n' +
                '• eksport z bazy (snake_case): [{ "name", "city", "date", ... }]\n' +
                '• CSV z Facebooka (lista wydarzeń)'
              ));
              return;
            }
          }

          const { valid, skipped } = validateAndNormalizeImport(rawList);
          if (valid.length === 0) {
            reject(new Error(
              'Brak poprawnych wydarzeń.\n\n' +
              'Każde musi mieć nazwę oraz datę i miasto\n' +
              '(date/city albo utcStartDate + location.city / miasto w nazwie).'
            ));
            return;
          }
          if (skipped > 0) {
            alert(`Pominięto ${skipped} z ${rawList.length} wpisów (brak nazwy, miasta lub daty).`);
          }
          resolve(valid);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Nie udało się odczytać pliku'));
      reader.readAsText(file);
    });
  };

  const refreshEventsFromApi = async () => {
    const refreshedEvents = await apiService.getEvents();
    setEvents(refreshedEvents);
    setFilteredEvents(refreshedEvents);
  };

  const handleJsonImportReview = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const validEvents = await parseImportFile(file);
      setImportReviewQueue(validEvents);
    } catch (error) {
      alert('Błąd importu: ' + (error as Error).message);
    } finally {
      e.target.value = '';
    }
  };

  const handleJsonImport = async (
    e: React.ChangeEvent<HTMLInputElement>,
    mode: 'replace' | 'merge'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const validEvents = await parseImportFile(file);

      if (mode === 'replace') {
        const confirmed = window.confirm(
          `Import zastąpi WSZYSTKIE wydarzenia w bazie (${validEvents.length} nowych).\n\nKontynuować?`
        );
        if (!confirmed) return;

        await apiService.bulkUpdateEvents(validEvents);
        alert(`Zastąpiono bazę — ${validEvents.length} wydarzeń.`);
      } else {
        const confirmed = window.confirm(
          `Dodać nowe wydarzenia z pliku (${validEvents.length} w pliku)?\n\n` +
          'Istniejące w bazie NIE zostaną usunięte. Duplikaty (ten sam link lub nazwa+miasto+data) zostaną pominięte.'
        );
        if (!confirmed) return;

        const result = await apiService.mergeEvents(validEvents);
        alert(
          `Dodano ${result.added} nowych wydarzeń.` +
          (result.skipped > 0 ? ` Pominięto ${result.skipped} duplikatów.` : '')
        );
      }

      await refreshEventsFromApi();
    } catch (error) {
      alert('Błąd importu: ' + (error as Error).message);
    } finally {
      e.target.value = '';
    }
  };

  const clearAllEvents = () => {
    if (window.confirm('Czy na pewno chcesz usunąć wszystkie wydarzenia? Ta operacja jest nieodwracalna!')) {
      setEvents([]);
      setFilteredEvents([]);
      alert('Wszystkie wydarzenia zostały usunięte');
    }
  };

  const pruneOldEvents = async () => {
    try {
      const input = window.prompt('Usuń wydarzenia starsze niż ile dni? (0 = starsze niż dziś)', '0');
      if (input === null) return;
      const days = parseInt(input, 10);
      const result = await apiService.pruneEvents(Number.isFinite(days) ? days : 0);
      const refreshed = await apiService.getEvents();
      setEvents(refreshed);
      setFilteredEvents(refreshed);
      alert(`Usunięto ${result.removed} wydarzeń. Pozostało: ${result.remaining}. Próg: ${result.threshold}`);
    } catch (e) {
      alert('Nie udało się wyczyścić starych wydarzeń');
      console.error(e);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login">
        <div className="admin-login-form">
          <h1>🔐 Panel Administracyjny</h1>
          <p>Dostęp tylko dla administratorów</p>
          
          {showLoginForm && (
            <LoginForm
              onLogin={handleLoginSuccess}
              onClose={handleLoginCancel}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>🔧 Panel Administracyjny</h1>
        <div className="admin-actions">
          <button onClick={handleLogout} className="logout-btn">
            🚪 Wyloguj
          </button>
          <button onClick={() => navigate('/')} className="view-site-btn">
            👁️ Zobacz stronę
          </button>
        </div>
      </div>

      {/* Zakładki */}
      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          📅 Eventy ({events.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'submissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('submissions')}
        >
          📝 Zgłoszenia ({submissions.filter(s => s.status === 'pending').length})
        </button>
        <button 
          onClick={() => setShowEventForm(true)}
          className="tab-btn add-event-tab-btn"
        >
          ➕ Dodaj nowy event
        </button>
      </div>

      <div className="admin-controls">
        <button 
          onClick={() => window.location.reload()}
          className="refresh-btn"
        >
          🔄 Odśwież stronę
        </button>
        
        <button 
          onClick={exportEventsToJSON}
          className="export-btn"
        >
          📥 Eksportuj do JSON
        </button>
        
        <label className="import-btn import-btn-review">
          📋 Import z podglądem
          <input
            type="file"
            accept=".json,.csv"
            onChange={handleJsonImportReview}
            style={{ display: 'none' }}
          />
        </label>

        <label className="import-btn import-btn-merge">
          ➕ Dodaj z JSON/CSV
          <input
            type="file"
            accept=".json,.csv"
            onChange={(e) => handleJsonImport(e, 'merge')}
            style={{ display: 'none' }}
          />
        </label>

        <label className="import-btn import-btn-replace">
          📤 Zastąp bazę z JSON/CSV
          <input
            type="file"
            accept=".json,.csv"
            onChange={(e) => handleJsonImport(e, 'replace')}
            style={{ display: 'none' }}
          />
        </label>
        
        <button 
          onClick={clearAllEvents}
          className="clear-btn"
        >
          🗑️ Wyczyść wszystkie wydarzenia
        </button>

        <button 
          onClick={pruneOldEvents}
          className="clear-btn"
        >
          🧹 Usuń stare wydarzenia
        </button>
      </div>

      {/* Zawartość zakładek */}
      {activeTab === 'events' && (
        <div className="tab-content">
          {/* Panel wyszukiwania */}
          <div className="admin-search-panel">
            <div className="search-content">
              <h3>Wyszukaj wydarzenia</h3>
              
              <div className="search-fields">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Wpisz nazwę artysty lub zespołu..."
                  className="search-input"
                />
                
                <div className="filter-row">
                  <select
                    value={filters.city}
                    onChange={(e) => handleFilterChange('city', e.target.value)}
                    className="filter-select"
                  >
                    <option value="">Wszystkie miasta</option>
                    {allCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>

                  <select
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    className="filter-select"
                  >
                    <option value="">Wszystkie typy</option>
                    {eventTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <input
                  type="date"
                  value={filters.date}
                  onChange={(e) => handleFilterChange('date', e.target.value)}
                  className="date-input"
                />

                <button onClick={clearFilters} className="clear-filters-btn">
                  🗑️ Wyczyść filtry
                </button>
              </div>
            </div>
          </div>

          {/* Lista wydarzeń */}
          <div className="events-list">
            <h2>Lista eventów ({filteredEvents.length} z {events.length})</h2>
            
            <div className="events-grid">
              {filteredEvents.map(event => (
                <div key={event.id} className="event-card">
                  <div className="event-header">
                    <h3>{event.name}</h3>
                    <div className="event-actions">
                      <button 
                        onClick={() => editEvent(event)}
                        className="edit-btn"
                      >
                        ✏️
                      </button>
                      <button 
                        onClick={() => deleteEvent(event.id)}
                        className="delete-btn"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div className="event-details">
                    <p><strong>Miasto:</strong> {event.city}</p>
                    <p><strong>Data:</strong> {event.date}</p>
                    <p><strong>Typ:</strong> {event.type}</p>
                    {event.price && event.showPrice && <p><strong>Cena:</strong> {event.price}</p>}
                    {event.address && <p><strong>Adres:</strong> {event.address}</p>}
                    {event.description && <p><strong>Opis:</strong> {event.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'submissions' && (
        <div className="tab-content">
          <div className="submissions-list">
            <h2>Zgłoszenia wydarzeń</h2>
            {submissions.filter(s => s.status === 'pending').map(submission => (
              <div key={submission.id} className="submission-card">
                <div className="submission-header">
                  <h3>{submission.name}</h3>
                  <div className="submission-actions">
                    <button
                      type="button"
                      onClick={() => editSubmission(submission)}
                      className="edit-btn"
                      title="Edytuj zgłoszenie"
                    >
                      ✏️
                    </button>
                    <button 
                      onClick={() => handleApproveSubmission(submission.id)} 
                      className="approve-btn"
                    >
                      ✅ Zatwierdź
                    </button>
                    <button 
                      onClick={() => handleRejectSubmission(submission.id)} 
                      className="reject-btn"
                    >
                      ❌ Odrzuć
                    </button>
                  </div>
                </div>
                <div className="submission-details">
                  <p><strong>Miasto:</strong> {submission.city || '—'}</p>
                  <p><strong>Data:</strong> {submission.date}</p>
                  {submission.time && <p><strong>Godzina:</strong> {submission.time}</p>}
                  {submission.type && <p><strong>Typ:</strong> {submission.type}</p>}
                  {submission.address && <p><strong>Adres:</strong> {submission.address}</p>}
                  {submission.description && <p><strong>Opis:</strong> {submission.description}</p>}
                  {submission.link && <p><strong>Link:</strong> <a href={submission.link} target="_blank" rel="noopener noreferrer">{submission.link}</a></p>}
                  <p><strong>Data zgłoszenia:</strong> {new Date(submission.createdAt).toLocaleString('pl-PL')}</p>
                </div>
              </div>
            ))}
            {submissions.filter(s => s.status === 'pending').length === 0 && (
              <div className="no-submissions">
                <p>Brak oczekujących zgłoszeń</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showEventForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <EventForm
              onAddEvent={addEvent}
              onUpdateEvent={() => {}}
              onClose={() => setShowEventForm(false)}
              editingEvent={null}
            />
          </div>
        </div>
      )}

      {editingEvent && (
        <div className="modal-overlay">
          <div className="modal-content">
            <EventForm
              onAddEvent={() => {}}
              onUpdateEvent={updateEvent}
              onClose={() => setEditingEvent(null)}
              editingEvent={editingEvent}
            />
          </div>
        </div>
      )}

      {editingSubmission && (
        <div className="modal-overlay">
          <div className="modal-content">
            <EventForm
              onAddEvent={() => {}}
              onUpdateEvent={updateSubmission}
              onClose={() => setEditingSubmission(null)}
              editingEvent={{
                id: editingSubmission.id,
                name: editingSubmission.name,
                city: editingSubmission.city || '',
                date: editingSubmission.date,
                time: editingSubmission.time,
                type: editingSubmission.type || 'Festiwal',
                price: editingSubmission.price,
                showPrice: editingSubmission.showPrice,
                address: editingSubmission.address,
                link: editingSubmission.link,
                description: editingSubmission.description,
                isMultiDay: editingSubmission.isMultiDay,
                endDate: editingSubmission.endDate,
              }}
              isSimple
            />
          </div>
        </div>
      )}

      {importReviewQueue && (
        <ImportReviewPanel
          initialItems={importReviewQueue}
          existingEvents={events}
          onClose={() => setImportReviewQueue(null)}
          onImported={refreshEventsFromApi}
        />
      )}
    </div>
  );
};

export default Admin;
